import React, { useEffect, useRef, useState } from "react";
import axios from "../api/axiosInstance";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import {
  currencyAmountStep,
  getCurrencyMinorUnits,
  hasValidCurrencyPrecision,
} from "../currency/currencyPrecision";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "var(--erp-overlay, rgba(7, 15, 36, 0.78))",
  zIndex: 22000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "4px 12px",
};

const shellStyleBase = {
  width: "100%",
  maxWidth: "640px",
  borderRadius: "16px",
  padding: "16px 20px",
  background: "var(--erp-modal-bg, #0e1730)",
  border: "1px solid var(--erp-modal-border, rgba(99, 193, 199, 0.2))",
  borderTop: "3px solid #3DB6E9",
  boxShadow: "var(--erp-modal-shadow, 0 18px 44px rgba(2, 8, 28, 0.28))",
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
};

const contentWrapperStyle = {};

const sectionStyle = {
  marginTop: "10px",
  borderRadius: "12px",
  padding: "14px",
  background: "var(--erp-modal-section, #121f3d)",
  border: "1px solid var(--erp-modal-border, rgba(99, 193, 199, 0.2))",
};

const sectionHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  alignItems: "flex-start",
  flexWrap: "wrap",
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: "1.05rem",
  fontWeight: 800,
  color: "var(--erp-modal-title, #f2f6ff)",
};

const sectionTextStyle = {
  margin: "4px 0 0",
  fontSize: "0.78rem",
  color: "var(--erp-modal-muted, #aebbd0)",
};

const tabContainerStyle = {
  display: "flex",
  background: "var(--erp-modal-section, #121f3d)",
  borderRadius: "12px",
  padding: "4px",
  border: "1px solid var(--erp-modal-border-strong, rgba(61, 182, 233, 0.62))",
};

const tabButtonStyle = (active) => ({
  flex: 1,
  borderRadius: "10px",
  border: "none",
  padding: "10px",
  fontWeight: 700,
  fontSize: "0.85rem",
  cursor: "pointer",
  color: active ? "#ffffff" : "var(--erp-modal-text, #dfe8f7)",
  background: active ? "var(--erp-modal-primary, #223A7A)" : "transparent",
  boxShadow: active ? "inset 3px 0 0 var(--erp-modal-primary-border, #3DB6E9)" : "none",
  transition: "all 0.2s ease",
});

const fieldGridStyle = {
  marginTop: "12px",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "12px",
};

const labelStyle = {
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "var(--erp-modal-text, #dfe8f7)",
  marginBottom: "4px",
  display: "block",
};

const inputFieldStyle = {
  width: "100%",
  borderRadius: "10px",
  padding: "10px 12px",
  background: "var(--erp-modal-input, #152344)",
  border: "1px solid var(--erp-modal-border-strong, rgba(61, 182, 233, 0.62))",
  color: "var(--erp-modal-text, #dfe8f7)",
  fontSize: "0.95rem",
};

const priceListStyle = {
  marginTop: "12px",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const priceRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 12px",
  borderRadius: "12px",
  background: "var(--erp-modal-section, #121f3d)",
  border: "1px solid var(--erp-modal-border, rgba(99, 193, 199, 0.2))",
};

const actionsRowStyle = {
  marginTop: "16px",
  display: "flex",
  gap: "12px",
};

const baseButtonStyle = {
  borderRadius: "12px",
  padding: "12px 26px",
  fontWeight: 700,
  letterSpacing: "0.03em",
  cursor: "pointer",
  flex: 1,
  border: "none",
};

const primaryButtonStyle = {
  ...baseButtonStyle,
  border: "1px solid var(--erp-modal-primary-border, #3DB6E9)",
  background: "var(--erp-modal-primary, #223A7A)",
  color: "var(--erp-modal-primary-text, #ffffff)",
};

const secondaryButtonStyle = {
  ...baseButtonStyle,
  border: "1px solid var(--erp-modal-border-strong, rgba(61, 182, 233, 0.62))",
  background: "var(--erp-modal-bg, #0e1730)",
  color: "var(--erp-modal-text, #dfe8f7)",
};

const toastOverlayStyle = {
  position: "fixed",
  top: "14px",
  left: 0,
  right: 0,
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  zIndex: 25000,
};

const toastCardStyle = {
  background: "var(--erp-modal-primary, #223A7A)",
  border: "1px solid #3DB6E9",
  borderRadius: "18px",
  padding: "14px 24px",
  color: "#ffffff",
  fontWeight: 700,
  boxShadow: "var(--erp-modal-shadow, 0 18px 44px rgba(2, 8, 28, 0.28))",
  maxWidth: "420px",
  width: "min(90vw, 420px)",
  textAlign: "center",
};

const savingOverlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "var(--erp-overlay, rgba(7, 15, 36, 0.78))",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
  fontWeight: "bold",
  zIndex: 23000,
  pointerEvents: "none",
};

const MODE_TABS = [
  { key: "single", label: "Single Date" },
  { key: "range", label: "Range" },
  { key: "customRange", label: "Variable Range Entry" },
];

const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const MAX_VARIABLE_DAYS = 30;

const parseIsoLocalDate = (raw) => {
  const str = String(raw || "").slice(0, 10);
  const [year, month, day] = str.split("-").map((n) => Number(n));
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  const date = new Date(year, month - 1, day);
  if (!Number.isFinite(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const toIsoLocalDate = (date) => {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Count calendar days without adding an extra day at the end of daylight saving time.
const inclusiveDayCount = (start, end) => {
  const startDay = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const endDay = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return (endDay - startDay) / (24 * 60 * 60 * 1000) + 1;
};

const buildIsoDateRange = (startValue, endValue) => {
  const start = parseIsoLocalDate(startValue);
  const end = parseIsoLocalDate(endValue);
  if (!start || !end || end < start) return [];

  const out = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    out.push(toIsoLocalDate(d));
  }
  return out;
};

const getRatePriceDay = (row) => String(row?.stay_date || row?.start_date || "").slice(0, 10);

const priceLooksApplied = (actual, expected) => {
  const actualNumber = Number(actual);
  const expectedNumber = Number(expected);
  if (!Number.isFinite(actualNumber) || !Number.isFinite(expectedNumber)) return false;

  return (
    Math.abs(actualNumber - expectedNumber) < 0.01 ||
    Math.abs(actualNumber - Math.round(expectedNumber)) < 0.01
  );
};

export const getRatePricePrecisionError = (values, currency, currencyCatalog = []) => {
  const code = String(currency || "").trim().toUpperCase();
  const invalidValue = (Array.isArray(values) ? values : [values]).some(
    (value) => value !== "" && value !== null && value !== undefined &&
      Number.isFinite(Number(value)) &&
      !hasValidCurrencyPrecision(value, code, currencyCatalog)
  );
  if (!invalidValue) return null;
  const minorUnits = getCurrencyMinorUnits(code, currencyCatalog);
  return `${code} prices accept ${minorUnits === 0 ? "whole units only" : `up to ${minorUnits} decimal places`}.`;
};

export default function NewRatePricePopup({
  show,
  onClose,
  roomRate,
  derivedMode = false,
  linkedToText = "",
  calcText = "",
}) {
  const { baseCurrency, currencyCatalog = [] } = useCurrencyDisplay();
  const [formState, setFormState] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [listHeight, setListHeight] = useState(220);
  const [needsScroll, setNeedsScroll] = useState(false);
  const shellRef = useRef(null);
  const toastTimer = useRef(null);

  useEffect(() => {
    if (show && roomRate) {
      setFormState({
        mode: "single",
        start_date: "",
        end_date: "",
        price: "",
        selectedDates: [],
        room_rate_id: roomRate.id,
        room_rate: roomRate,
      });
      setToastMessage("");
    } else {
      setFormState(null);
      setToastMessage("");
      if (toastTimer.current) {
        clearTimeout(toastTimer.current);
        toastTimer.current = null;
      }
    }
  }, [show, roomRate]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const adjust = () => {
      if (formState?.mode === "customRange") {
        const candidate = Math.max(180, Math.min(window.innerHeight - 220, 420));
        setListHeight(candidate);
        if (shellRef.current) {
          const rect = shellRef.current.getBoundingClientRect();
          setNeedsScroll(rect.height > window.innerHeight - 24);
        } else {
          setNeedsScroll(false);
        }
      } else {
        setListHeight(220);
        setNeedsScroll(false);
      }
    };
    adjust();
    window.addEventListener("resize", adjust);
    return () => window.removeEventListener("resize", adjust);
  }, [formState?.mode, formState?.start_date, formState?.end_date]);

  useEffect(() => {
    return () => {
      if (toastTimer.current) {
        clearTimeout(toastTimer.current);
      }
    };
  }, []);

  if (!show || !formState) return null;

  const rateCurrency = String(roomRate?.currency || baseCurrency || "").trim().toUpperCase();
  const amountStep = currencyAmountStep(rateCurrency, currencyCatalog);

  const finishWithToast = (message) => {
    if (toastTimer.current) {
      clearTimeout(toastTimer.current);
    }
    setToastMessage(message);
    toastTimer.current = setTimeout(() => {
      setToastMessage("");
      if (onClose) onClose(true);
    }, 2000);
  };

  const verifyRatePricePayloadsApplied = async (payloads) => {
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/rate-prices/history/${formState.room_rate_id}`,
      { withCredentials: true }
    );

    const historyRows = Array.isArray(data) ? data : [];
    const rowsByDay = new Map(
      historyRows
        .filter((row) => String(row.room_rate_id) === String(formState.room_rate_id))
        .map((row) => [getRatePriceDay(row), row])
    );

    return payloads.every((payload) => {
      const day = String(payload.start_date || "").slice(0, 10);
      const row = rowsByDay.get(day);
      if (!row) return false;
      return priceLooksApplied(row.price, payload.price);
    });
  };

  const postRatePriceWithTimeoutFallback = async (payload, verificationPayloads = [payload]) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/rate-prices`, payload);
    } catch (error) {
      const isGatewayError = [502, 503, 504].includes(error?.response?.status);
      if (isGatewayError) {
        try {
          const alreadyApplied = await verifyRatePricePayloadsApplied(verificationPayloads);
          if (alreadyApplied) return;
        } catch (verificationError) {
          const verificationIsGatewayError = [502, 503, 504].includes(verificationError?.response?.status);
          if (verificationIsGatewayError) {
            // The save likely succeeded but the server is still busy processing.
            // The database has been updated correctly; return without error.
            console.warn("Rate price save timed out but likely succeeded (verification also unavailable).");
            return;
          }
          console.error("Error verifying rate price save after timeout:", verificationError);
        }
      }
      throw error;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSaving || toastMessage) return;
    setIsSaving(true);
    try {
      if (derivedMode) {
        const start = parseIsoLocalDate(formState.start_date);
        const end = parseIsoLocalDate(formState.end_date);
        const validFrom = parseIsoLocalDate(roomRate?.valid_from);
        const validTo = parseIsoLocalDate(roomRate?.valid_to);
        if (!start || !end) {
          throw new Error("Please select valid start and end dates.");
        }
        if (end < start) {
          throw new Error("End date must be on or after start date.");
        }
        if ((validFrom && start < validFrom) || (validTo && end > validTo)) {
          throw new Error("Dates must stay within the room rate validity period.");
        }
        if (formState.mode === "customRange" && inclusiveDayCount(start, end) > MAX_VARIABLE_DAYS) {
          throw new Error("Maximum 30 days allowed.");
        }
        const rangeDates = buildIsoDateRange(formState.start_date, formState.end_date);
        const dates = formState.mode === "customRange"
          ? rangeDates.filter((date) => formState.selectedDates.includes(date))
          : rangeDates;
        if (!dates.length) {
          throw new Error("Select at least one date to calculate prices.");
        }
        const { data } = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/derived-rates/${formState.room_rate_id}/apply-prices`,
          { dates }
        );
        setIsSaving(false);
        finishWithToast(data?.message || `Prices calculated for ${dates.length} selected date${dates.length === 1 ? "" : "s"}.`);
        return;
      }

      if (formState.mode === "customRange") {
        const start = parseIsoLocalDate(formState.start_date);
        const end = parseIsoLocalDate(formState.end_date);
        if (!start || !end) {
          alert("Please select valid start and end dates.");
          setIsSaving(false);
          return;
        }
        if (end < start) {
          alert("End date must be after start date.");
          setIsSaving(false);
          return;
        }
        const diffDays = inclusiveDayCount(start, end);
        if (diffDays > MAX_VARIABLE_DAYS) {
          alert("Maximum 30 days allowed.");
          setIsSaving(false);
          return;
        }
        const days = Array.from({ length: diffDays }, (_, idx) => {
          const date = new Date(start);
          date.setDate(start.getDate() + idx);
          return toIsoLocalDate(date);
        });
        const payloads = [];
        const precisionError = getRatePricePrecisionError(
          days.map((_, index) => formState[`multi_price_${index}`]),
          rateCurrency,
          currencyCatalog
        );
        if (precisionError) {
          alert(precisionError);
          setIsSaving(false);
          return;
        }
        for (let i = 0; i < days.length; i += 1) {
          const key = `multi_price_${i}`;
          const raw = formState[key];
          const price = Number.parseFloat(String(raw || "").trim());
          if (!Number.isFinite(price)) continue;
          payloads.push({
            room_rate_id: formState.room_rate_id,
            start_date: days[i],
            end_date: days[i],
            price,
          });
        }
        if (!payloads.length) {
          alert("No valid days with price to save.");
          setIsSaving(false);
          return;
        }
        for (const payload of payloads) {
          await postRatePriceWithTimeoutFallback(payload);
        }
        setIsSaving(false);
        finishWithToast(`${payloads.length} rate prices added successfully.`);
        return;
      }

      const start = parseIsoLocalDate(formState.start_date);
      const end = parseIsoLocalDate(formState.end_date);
      const validFrom = parseIsoLocalDate(formState.room_rate?.valid_from);
      const validTo = parseIsoLocalDate(formState.room_rate?.valid_to);
      if (!start || !end) {
        alert("Please select valid start and end dates.");
        setIsSaving(false);
        return;
      }
      if (validFrom && start < validFrom) {
        alert("Dates must stay within the room rate validity period.");
        setIsSaving(false);
        return;
      }
      if (validTo && end > validTo) {
        alert("Dates must stay within the room rate validity period.");
        setIsSaving(false);
        return;
      }
      const price = Number.parseFloat(formState.price);
      if (!Number.isFinite(price)) {
        alert("Enter a valid price.");
        setIsSaving(false);
        return;
      }
      const precisionError = getRatePricePrecisionError(formState.price, rateCurrency, currencyCatalog);
      if (precisionError) {
        alert(precisionError);
        setIsSaving(false);
        return;
      }
      const payload = {
        room_rate_id: formState.room_rate_id,
        start_date: formState.start_date,
        end_date: formState.end_date,
        price,
      };
      const verificationPayloads = buildIsoDateRange(formState.start_date, formState.end_date).map((day) => ({
        room_rate_id: formState.room_rate_id,
        start_date: day,
        end_date: day,
        price,
      }));
      await postRatePriceWithTimeoutFallback(payload, verificationPayloads);
      setIsSaving(false);
      finishWithToast("Rate price added successfully.");
    } catch (error) {
      console.error("Error saving Rate Price:", error);
      setIsSaving(false);
      alert(error?.response?.data?.error || (derivedMode ? error.message || "Error calculating derived prices." : "Error saving Rate Price."));
    }
  };

  const dynamicShellStyle = {
    ...shellStyleBase,
    maxHeight: "calc(100dvh - 8px)",
  };

  const variableListStyles = {
    ...priceListStyle,
    maxHeight: `${listHeight}px`,
    overflowY: "auto",
    paddingRight: "6px",
  };

  const handleDateChange = (value, field) => {
    setFormState((prev) => {
      const next = { ...prev, [field]: value, selectedDates: [] };
      if (field === "start_date" && prev.mode === "single") {
        next.end_date = value;
      }
      return next;
    });
  };

  return (
    <div style={overlayStyle}>
      <div style={dynamicShellStyle} ref={shellRef} role="dialog" aria-modal="true" aria-busy={isSaving} aria-labelledby="rate-price-popup-title">
        <div style={{ textAlign: "center", marginBottom: "12px" }}>
          <p
            style={{
              letterSpacing: "0.35em",
              fontSize: "0.8rem",
              color: "var(--erp-modal-muted, #aebbd0)",
              marginBottom: "6px",
              textTransform: "uppercase",
            }}
          >
            Room rate studio
          </p>
          <h2 id="rate-price-popup-title" style={{ margin: 0, fontSize: "1.35rem", color: "var(--erp-modal-title, #f2f6ff)" }}>
            {derivedMode ? "Calculate prices from base rate" : "Add rate price"}
          </h2>
          <p style={{ marginTop: "6px", color: "var(--erp-modal-muted, #aebbd0)" }}>
            {roomRate?.name} ({roomRate?.rate_code})
          </p>
        </div>

        <div style={contentWrapperStyle}>
          <form onSubmit={handleSubmit}>
            <fieldset disabled={isSaving || Boolean(toastMessage)} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            {derivedMode && (
              <section style={sectionStyle}>
                <h3 style={sectionTitleStyle}>Base rate calculation</h3>
                <p style={{ ...sectionTextStyle, overflowWrap: "anywhere" }}>
                  Linked to: <strong>{linkedToText || `#${roomRate?.base_rate_id}`}</strong>
                  {calcText ? ` | Calc: ${calcText}` : ""}
                </p>
                <p style={sectionTextStyle}>
                  Apply the saved calculation to the base prices for the dates you choose.
                  Existing prices on those dates will be replaced. All required base prices must be available.
                </p>
              </section>
            )}
            <section style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <div>
                  <h3 style={sectionTitleStyle}>Mode & dates</h3>
                  <p style={sectionTextStyle}>
                    {derivedMode
                      ? "Choose a date or interval, or select individual days in a variable range. No dates are selected by default."
                      : "Choose how you want to add the price and supply the span."}
                  </p>
                </div>
              </div>

              <div style={tabContainerStyle}>
                {MODE_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    style={tabButtonStyle(formState.mode === tab.key)}
                    onClick={() =>
                      setFormState((prev) => ({
                        ...prev,
                        mode: tab.key,
                        selectedDates: [],
                        ...(tab.key === "single" ? { end_date: prev.start_date } : {}),
                      }))
                    }
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div style={fieldGridStyle}>
                <div>
                  <label htmlFor="rate-price-start-date" style={labelStyle}>{formState.mode === "single" ? "Date" : "Start date"}</label>
                  <input
                    id="rate-price-start-date"
                    type="date"
                    style={inputFieldStyle}
                    value={formState.start_date}
                    onChange={(event) => handleDateChange(event.target.value, "start_date")}
                    min={derivedMode ? roomRate?.valid_from?.slice(0, 10) : undefined}
                    max={derivedMode ? roomRate?.valid_to?.slice(0, 10) : undefined}
                    required
                  />
                </div>
                {(formState.mode === "range" || formState.mode === "customRange") && (
                  <div>
                    <label htmlFor="rate-price-end-date" style={labelStyle}>End date</label>
                    <input
                      id="rate-price-end-date"
                      type="date"
                      style={inputFieldStyle}
                      value={formState.end_date}
                      onChange={(event) => handleDateChange(event.target.value, "end_date")}
                      min={derivedMode ? formState.start_date || roomRate?.valid_from?.slice(0, 10) : undefined}
                      max={derivedMode ? roomRate?.valid_to?.slice(0, 10) : undefined}
                      required
                    />
                  </div>
                )}
              </div>
            </section>

            {!derivedMode && (formState.mode === "single" || formState.mode === "range") && (
              <section style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                  <div>
                    <h3 style={sectionTitleStyle}>Price</h3>
                    <p style={sectionTextStyle}>Enter the amount that applies for the selected interval.</p>
                  </div>
                </div>
                <div style={fieldGridStyle}>
                  <div>
                    <label style={labelStyle}>Price ({rateCurrency})</label>
                    <input
                      type="number"
                      step={amountStep}
                      style={inputFieldStyle}
                      value={formState.price}
                      onChange={(event) => setFormState((prev) => ({ ...prev, price: event.target.value }))}
                      required
                    />
                  </div>
                </div>
              </section>
            )}

            {formState.mode === "customRange" && formState.start_date && formState.end_date && (
              <section style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                  <div>
                    <h3 style={sectionTitleStyle}>{derivedMode ? "Dates to calculate" : "Variable range entries"}</h3>
                    <p style={sectionTextStyle}>
                      {derivedMode
                        ? `Select the days to apply the base rate calculation (max 30 days). ${formState.selectedDates.length} selected.`
                        : "Assign a price for each day in the window (max 30 days). Leave blank to skip a date."}
                    </p>
                  </div>
                </div>
                {(() => {
                  const start = parseIsoLocalDate(formState.start_date);
                  const end = parseIsoLocalDate(formState.end_date);
                  if (!start || !end) return null;
                  const diffDays = inclusiveDayCount(start, end);
                  if (Number.isNaN(diffDays) || diffDays <= 0) return null;
                  if (diffDays > MAX_VARIABLE_DAYS) {
                    return (
                      <div style={sectionTextStyle}>
                        Only up to {MAX_VARIABLE_DAYS} days can be entered at once.
                      </div>
                    );
                  }
                  const days = Array.from({ length: diffDays }, (_, idx) => {
                    const date = new Date(start);
                    date.setDate(start.getDate() + idx);
                    return date;
                  });
                  return (
                    <div style={variableListStyles}>
                      {days.map((date, idx) => {
                        const iso = toIsoLocalDate(date);
                        const weekday = WEEKDAY_NAMES[date.getDay()];
                        const isWeekend = date.getDay() === 5 || date.getDay() === 6;
                        return (
                          <div
                            key={iso}
                            style={{
                              ...priceRowStyle,
                              backgroundColor: isWeekend
                                ? "var(--erp-modal-accent-soft, rgba(61, 182, 233, 0.12))"
                                : "var(--erp-modal-bg, #0e1730)",
                            }}
                          >
                            <div style={{ fontSize: "0.9rem", color: "var(--erp-modal-text, #dfe8f7)" }}>
                              <strong>{iso}</strong> · {weekday}
                            </div>
                            {derivedMode ? (
                              <input
                                type="checkbox"
                                aria-label={`Apply price for ${iso}`}
                                checked={formState.selectedDates.includes(iso)}
                                style={{ width: 20, height: 20, marginLeft: 12, flexShrink: 0, accentColor: "#3DB6E9" }}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setFormState((prev) => ({
                                    ...prev,
                                    selectedDates: checked
                                      ? [...prev.selectedDates, iso]
                                      : prev.selectedDates.filter((day) => day !== iso),
                                  }));
                                }}
                              />
                            ) : <input
                              type="number"
                              step={amountStep}
                              inputMode={amountStep === "1" ? "numeric" : "decimal"}
                              placeholder="Price"
                              style={{ ...inputFieldStyle, width: "130px", marginLeft: "12px" }}
                              onChange={(event) =>
                                setFormState((prev) => ({
                                  ...prev,
                                  [`multi_price_${idx}`]: event.target.value,
                                }))
                              }
                            />}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </section>
            )}

            <div style={actionsRowStyle}>
              <button
                type="submit"
                style={{
                  ...primaryButtonStyle,
                  opacity: isSaving ? 0.75 : 1,
                  cursor: isSaving ? "not-allowed" : primaryButtonStyle.cursor,
                }}
                disabled={isSaving || Boolean(toastMessage)}
              >
                {derivedMode
                  ? (isSaving ? "Calculating..." : "Calculate prices")
                  : (isSaving ? "Saving..." : "Save rate price")}
              </button>
              <button
                type="button"
                style={{
                  ...secondaryButtonStyle,
                  opacity: isSaving ? 0.65 : 1,
                  cursor: isSaving ? "not-allowed" : secondaryButtonStyle.cursor,
                }}
                onClick={() => onClose && onClose(false)}
                disabled={isSaving}
              >
                Cancel
              </button>
            </div>
            </fieldset>
          </form>
        </div>
      </div>

      {isSaving && <div style={savingOverlayStyle}>{derivedMode ? "Calculating derived prices…" : "Saving rate price…"}</div>}

      {toastMessage && (
        <div style={toastOverlayStyle}>
          <div style={toastCardStyle} role="status">{toastMessage}</div>
        </div>
      )}
    </div>
  );
}
